import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Switch,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import { ActionsColumn, type IAction, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  PlayIcon,
  PlusCircleIcon,
  SyncAltIcon,
} from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAdminSplaBindings } from '../../../hooks/useAdminSplaBindings';
import { trimDecimalZeros } from '../../../utils/money';
import { formatRelativeDaysWallClock, formatWallClockDateTime } from '../../../utils/dateFormat';
import { SplaSubjectCell } from '../../utils/splaSubjects';
import type { SplaBinding } from '../../../types/models/Spla';
import type {
  ApiResponseSplaBinding,
  ApiResponseSplaBindingDeleted,
  ApiResponseSplaRecompute,
} from '../../../types/api/ApiResponseSpla';
import ClientTypeahead, { type ClientOption } from '../ProductsAndPrices/ClientTypeahead';
import SplaBindingModal, { SPLA_RULE_LABELS } from './SplaBindingModal';

interface DeleteModalProps {
  binding: SplaBinding;
  onClose: () => void;
  /** Performs the DELETE; resolves `true` on success (the parent owns the toasts). */
  onDelete: (binding: SplaBinding) => Promise<boolean>;
}

function SplaBindingDeleteModal({ binding, onClose, onDelete }: DeleteModalProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    setIsDeleting(true);
    const ok = await onDelete(binding);
    setIsDeleting(false);
    if (ok) {
      onClose();
    }
  };

  const subjectName = binding.subject.label ?? binding.subject.ref;

  return (
    <Modal variant="small" isOpen onClose={onClose} aria-label="Delete binding">
      <ModalHeader title="Delete binding" titleIconVariant="warning" />
      <ModalBody>
        <Content component="p">
          Delete the binding of <strong>{binding.license.part_number ?? `license #${binding.license.id}`}</strong>{' '}
          to <strong>{subjectName}</strong> for {binding.client.name ?? `client #${binding.client.id}`}?
        </Content>
        <Content component="p">
          This removes the binding AND its computed consumption row — user/agent rows on the same subject
          are untouched.
        </Content>
      </ModalBody>
      <ModalFooter>
        <Button variant="danger" onClick={() => void handleConfirm()} isLoading={isDeleting} isDisabled={isDeleting}>
          Delete
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isDeleting}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

interface SplaBindingsTabProps {
  /** Defers every admin fetch until the page's staff check has passed. */
  isStaff: boolean;
  /** Bumped by the Candidates tab when a binding was created from the queue — triggers a refetch. */
  refreshKey: number;
  /** Tell the Consumption tab its computed rows changed (recompute / create / edit / delete). */
  onConsumptionChanged: () => void;
  /** Tell the Candidates tab a subject's binding_count changed (create / delete). */
  onCandidatesChanged: () => void;
}

/**
 * The license ↔ subject attachments that auto-maintain `computed` consumption rows. A failing
 * binding keeps its PREVIOUS consumption row (a license never silently drops off the report),
 * stamps `last_error`, and shows the warning badge here.
 */
function SplaBindingsTab({ isStaff, refreshKey, onConsumptionChanged, onCandidatesChanged }: SplaBindingsTabProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [clientFilter, setClientFilter] = useState<number | null>(null);
  const { bindings, loading, error, refetch, applyBinding, removeBinding } = useAdminSplaBindings(
    isStaff,
    clientFilter,
    refreshKey,
  );

  // Client filter options accumulated from the rows (the /clients list fails during impersonation,
  // and server-side filtering would otherwise shrink the options to the selected client).
  const [clientNames, setClientNames] = useState<Map<number, string>>(new Map());
  useEffect(() => {
    setClientNames((prev) => {
      const next = new Map(prev);
      let changed = false;
      bindings.forEach((binding) => {
        const name = binding.client.name ?? `Client #${binding.client.id}`;
        if (next.get(binding.client.id) !== name) {
          next.set(binding.client.id, name);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [bindings]);

  const clientOptions = useMemo<ClientOption[]>(
    () =>
      Array.from(clientNames.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [clientNames],
  );

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editBinding, setEditBinding] = useState<SplaBinding | null>(null);
  const [deleteBinding, setDeleteBinding] = useState<SplaBinding | null>(null);
  const [isRecomputing, setIsRecomputing] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const handleSaved = useCallback(
    (binding: SplaBinding, isCreate: boolean) => {
      if (isCreate) {
        void refetch();
        // A new subject binding bumps the candidate queue's binding_count.
        onCandidatesChanged();
      } else {
        applyBinding(binding);
      }
      // Create computes once; an edit's rule/activity change recomputes immediately.
      onConsumptionChanged();
    },
    [refetch, applyBinding, onConsumptionChanged, onCandidatesChanged],
  );

  // POST /admin/spla/bindings/recompute — recompute every ACTIVE binding now, then refresh both
  // this list (health columns) and the Consumption tab (the computed rows).
  const handleRecompute = useCallback(async () => {
    setIsRecomputing(true);
    try {
      const response = await apiFetch('/admin/spla/bindings/recompute', { method: 'POST' });
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseSplaRecompute | null;
      if (body?.status === 'success' && body.data?.recompute) {
        const { computed, failed, skipped_inactive: skipped } = body.data.recompute;
        addNotification({
          title: failed > 0 ? 'Recompute finished with failures' : 'Recompute finished',
          srTitle: failed > 0 ? 'Warning alert' : 'Success alert',
          description: `${computed} computed, ${failed} failed, ${skipped} inactive skipped.${failed > 0 ? ' Failing bindings keep their previous consumption row — check the warning badges.' : ''}`,
          variant: failed > 0 ? 'warning' : 'success',
        });
        setDrawerOpen(true);
        void refetch();
        onConsumptionChanged();
      } else {
        addNotification({
          title: 'Could not recompute the bindings',
          srTitle: 'Error alert',
          description: body?.message ?? 'Could not recompute the bindings. Please try again.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      }
    } catch (err) {
      addNotification({
        title: 'Could not recompute the bindings',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsRecomputing(false);
    }
  }, [apiFetch, addNotification, setDrawerOpen, refetch, onConsumptionChanged]);

  // PATCH { active } — an activity change recomputes immediately server-side; the response carries
  // the refreshed row (and a compute_error when reactivation failed to resolve).
  const handleToggleActive = useCallback(
    async (binding: SplaBinding, next: boolean) => {
      setTogglingId(binding.id);
      try {
        const response = await apiFetch(`/admin/spla/bindings/${binding.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ active: next }),
        });
        const body = (await response.json().catch(() => null)) as ApiResponseSplaBinding | null;
        if (body?.status === 'success' && body.data?.binding) {
          applyBinding(body.data.binding);
          const computeError = body.data.compute_error ?? null;
          addNotification({
            title: next
              ? computeError !== null
                ? 'Binding activated, compute failed'
                : 'Binding activated'
              : 'Binding deactivated',
            srTitle: computeError !== null ? 'Warning alert' : 'Success alert',
            description:
              computeError !== null
                ? `${computeError} — fix the cause, then recompute.`
                : next
                  ? 'It recomputed immediately.'
                  : 'Its consumption row is frozen as-is (human-owned again).',
            variant: computeError !== null ? 'warning' : 'success',
          });
          setDrawerOpen(true);
          onConsumptionChanged();
        } else {
          addNotification({
            title: 'Could not update the binding',
            srTitle: 'Error alert',
            description: body?.message ?? 'Could not update the binding. Please try again.',
            variant: 'danger',
          });
          setDrawerOpen(true);
        }
      } catch (err) {
        addNotification({
          title: 'Could not update the binding',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      } finally {
        setTogglingId(null);
      }
    },
    [apiFetch, applyBinding, addNotification, setDrawerOpen, onConsumptionChanged],
  );

  const handleDelete = useCallback(
    async (binding: SplaBinding): Promise<boolean> => {
      try {
        const response = await apiFetch(`/admin/spla/bindings/${binding.id}`, { method: 'DELETE' });
        const body = (await response.json().catch(() => null)) as ApiResponseSplaBindingDeleted | null;
        if (body?.status === 'success' && body.data?.deleted) {
          removeBinding(binding.id);
          addNotification({
            title: 'Binding deleted',
            srTitle: 'Success alert',
            description: 'The binding and its computed consumption row were removed.',
            variant: 'success',
          });
          setDrawerOpen(true);
          onConsumptionChanged();
          onCandidatesChanged();
          return true;
        }
        addNotification({
          title: 'Could not delete the binding',
          srTitle: 'Error alert',
          description: body?.message ?? 'Could not delete the binding. Please try again.',
          variant: 'danger',
        });
        setDrawerOpen(true);
        return false;
      } catch (err) {
        addNotification({
          title: 'Could not delete the binding',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
        return false;
      }
    },
    [apiFetch, removeBinding, addNotification, setDrawerOpen, onConsumptionChanged, onCandidatesChanged],
  );

  const rowActions = (binding: SplaBinding): IAction[] => [
    {
      title: 'Edit',
      onClick: () => setEditBinding(binding),
    },
    {
      title: 'Delete',
      isDanger: true,
      onClick: () => setDeleteBinding(binding),
    },
  ];

  const renderHealthCell = (binding: SplaBinding) => (
    <>
      {binding.last_computed_at !== null ? (
        <Tooltip content={`Last computed ${formatWallClockDateTime(binding.last_computed_at)}`}>
          <span>
            {formatRelativeDaysWallClock(binding.last_computed_at) ??
              formatWallClockDateTime(binding.last_computed_at)}
          </span>
        </Tooltip>
      ) : (
        <Content component="small">never computed</Content>
      )}
      {binding.last_error !== null && (
        <>
          {' '}
          <Tooltip content={binding.last_error}>
            <Label color="orange" icon={<ExclamationTriangleIcon />} isCompact>
              error
            </Label>
          </Tooltip>
        </>
      )}
    </>
  );

  const renderContent = () => {
    if (loading && bindings.length === 0) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load the bindings"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (bindings.length === 0 && clientFilter === null) {
      return (
        <EmptyState titleText="No bindings yet" icon={LinkIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            Bind a license to a subject and its consumed quantity is computed from physical cores (or a
            fixed count) on every recompute.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsCreateOpen(true)}>
                Create binding
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (bindings.length === 0) {
      return (
        <EmptyState titleText="No bindings for this client" icon={LinkIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>Clear the client filter to see every binding.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => setClientFilter(null)}>
                Clear filter
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <Table aria-label="SPLA bindings" variant="compact">
        <Thead>
          <Tr>
            <Th>Client</Th>
            <Th>License</Th>
            <Th>Subject</Th>
            <Th>Rule</Th>
            <Th>Active</Th>
            <Th>Health</Th>
            <Th>Note</Th>
            <Th screenReaderText="Row actions" />
          </Tr>
        </Thead>
        <Tbody>
          {bindings.map((binding) => (
            <Tr key={binding.id}>
              <Td dataLabel="Client">{binding.client.name ?? `Client #${binding.client.id}`}</Td>
              <Td dataLabel="License">
                {binding.license.part_number ?? `#${binding.license.id}`}
                <div>
                  <Content component="small">{binding.license.product_family ?? ''}</Content>
                </div>
              </Td>
              <Td dataLabel="Subject">
                <SplaSubjectCell
                  type={binding.subject.type}
                  subjectRef={binding.subject.ref}
                  name={binding.subject.label ?? binding.subject.ref}
                />
              </Td>
              <Td dataLabel="Rule">
                {SPLA_RULE_LABELS[binding.rule]}
                {binding.rule === 'fixed' && binding.fixed_quantity !== null && (
                  <> · {trimDecimalZeros(binding.fixed_quantity)}</>
                )}
              </Td>
              <Td dataLabel="Active">
                <Switch
                  id={`spla-binding-active-${binding.id}`}
                  aria-label={`Toggle whether the binding for ${binding.subject.label ?? binding.subject.ref} recomputes`}
                  isChecked={binding.active}
                  onChange={(_event, checked) => void handleToggleActive(binding, checked)}
                  isDisabled={togglingId !== null}
                />
              </Td>
              <Td dataLabel="Health">{renderHealthCell(binding)}</Td>
              <Td dataLabel="Note">
                {binding.note !== null ? <Content component="small">{binding.note}</Content> : ''}
              </Td>
              <Td isActionCell>
                <ActionsColumn items={rowActions(binding)} />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  };

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem style={{ minWidth: '16rem' }}>
            <ClientTypeahead
              clients={clientOptions}
              value={clientFilter}
              onChange={setClientFilter}
              idPrefix="spla-bindings-filter-client"
            />
          </ToolbarItem>
          <ToolbarGroup align={{ default: 'alignEnd' }}>
            <ToolbarItem>
              <Button
                variant="primary"
                icon={<PlusCircleIcon />}
                onClick={() => setIsCreateOpen(true)}
                isDisabled={loading && bindings.length === 0}
              >
                Create binding
              </Button>
            </ToolbarItem>
            <ToolbarItem>
              <Button
                variant="secondary"
                icon={<PlayIcon />}
                onClick={() => void handleRecompute()}
                isLoading={isRecomputing}
                isDisabled={isRecomputing}
              >
                Recompute all
              </Button>
            </ToolbarItem>
            <ToolbarItem>
              <Button
                variant="secondary"
                icon={<SyncAltIcon />}
                onClick={() => void refetch()}
                isLoading={loading}
                isDisabled={loading}
              >
                Refresh
              </Button>
            </ToolbarItem>
          </ToolbarGroup>
        </ToolbarContent>
      </Toolbar>
      {renderContent()}
      {isCreateOpen && (
        <SplaBindingModal isOpen binding={null} onClose={() => setIsCreateOpen(false)} onSaved={handleSaved} />
      )}
      {editBinding && (
        <SplaBindingModal
          key={editBinding.id}
          isOpen
          binding={editBinding}
          onClose={() => setEditBinding(null)}
          onSaved={handleSaved}
        />
      )}
      {deleteBinding && (
        <SplaBindingDeleteModal
          binding={deleteBinding}
          onClose={() => setDeleteBinding(null)}
          onDelete={handleDelete}
        />
      )}
    </>
  );
}

export default SplaBindingsTab;
